/*
 * ----------------------------------------------------------------------------------------------------------------
 * USA BATS GRID FILTER
 * ----------------------------------------------------------------------------------------------------------------
 *
 * Client-side drop/length filter for sections/usa-bats-grid.liquid. Deliberately a separate asset file (not
 * appended to custom.js), same isolation precedent as assets/swatch-swap.js — a shared-file edit already broke
 * both BBCOR's and USA's Add to Cart button once this project (see _shared/WARSTIC_BBCOR_STATUS.md, "Bug found
 * and fixed: Add to Cart button had zero styling"), so new substantial JS gets its own file rather than growing
 * that risk surface.
 *
 * STAKEHOLDER REQUIREMENTS UPDATE, 2026-07-31 (superseding the original color+drop+length build):
 * - Color filter removed entirely. Only drop and length remain.
 * - "Grid reshaping" (must-have): a length filter must narrow which EDITIONS show as swatches within a still-
 *   visible line card — hide/disable the swatch for any edition that doesn't have the selected length in
 *   stock, and auto-swap the displayed default if it's no longer valid — rather than just hiding/showing whole
 *   flat product cards. This replaces the old sibling-swap-by-color logic entirely.
 * - Custom Bats now use the same "hidden once any filter is active" rule Bat Grips used to have on this page.
 * - Bat Grips are no longer in this DOM/JS's scope at all — they moved to their own separate section
 *   ('usa-bats-grips-grid.liquid'), genuinely untouched by this file.
 *
 * Does NOT reimplement colorway-swap visuals (image crossfade, colorway label, size-overlay rebuild) — that's
 * already handled by the existing BBCOR swap logic in custom.js, which delegates off a document-level 'change'
 * listener on `[data-bbcor-card] .product-card__info [type="radio"]`. This file's cards use the identical DOM
 * contract (data-bbcor-card, same radio/label structure, data-edition-handle on each swatch), so triggering a
 * sibling swap here is just: check the matching radio and dispatch a bubbling 'change' event — the existing
 * listener does the rest. Hiding/showing individual swatches is this file's own concern (narrowSwatches below).
 *
 * Card categories (see snippets/usa-bats-line-card.liquid and snippets/usa-bats-plain-card.liquid):
 * - 'line'           — Hybrid (x3 drop-tier cards) / Metal-11. Card-level drop + per-sibling length data.
 * - 'teeball'        — plain card, real drop + length data, no swatches (single colorway).
 * - 'hide-on-filter' — Custom Bats. Visible by default, hidden the instant any filter is active.
 */

(function () {
	'use strict';

	function init() {
		var root = document.querySelector('[data-usa-bats-root]');
		if (!root) return;

		var cards = buildCardModel(root);
		var filterInputs = Array.prototype.slice.call(root.querySelectorAll('[data-usa-bats-filter]'));
		var emptyState = root.querySelector('[data-usa-bats-empty-state]');

		filterInputs.forEach(function (input) {
			input.addEventListener('change', function () {
				applyFilters(cards, filterInputs, emptyState);
			});
		});

		applyFilters(cards, filterInputs, emptyState);
	}

	function buildCardModel(root) {
		var cardEls = Array.prototype.slice.call(root.querySelectorAll('[data-usa-bats-card]'));

		return cardEls.map(function (el) {
			if (el.hasAttribute('data-usa-bats-hide-on-filter')) {
				return { el: el, type: 'hide-on-filter' };
			}

			var facetsScript = el.querySelector('[data-usa-bats-facets]');
			var facets = {};
			if (facetsScript) {
				try {
					facets = JSON.parse(facetsScript.textContent);
				} catch (e) {
					facets = {};
				}
			}

			if (el.hasAttribute('data-drop')) {
				// Line card (usa-bats-line-card) — collapsed swatch-swap group.
				return {
					el: el,
					type: 'line',
					drop: normalize(el.getAttribute('data-drop')),
					siblings: facets.siblings || {},
					currentHandle: el.getAttribute('handle')
				};
			}

			// Plain teeball card.
			return {
				el: el,
				type: 'teeball',
				drop: normalize(facets.drop),
				lengths: (facets.lengths || []).map(normalize)
			};
		});
	}

	function normalize(value) {
		return (value || '').toString().trim().toLowerCase();
	}

	function readActiveState(filterInputs) {
		var state = { length: new Set(), drop: new Set() };

		filterInputs.forEach(function (input) {
			if (!input.checked) return;
			var facet = input.getAttribute('data-usa-bats-filter');
			if (state[facet]) state[facet].add(normalize(input.value));
		});

		return state;
	}

	// Which sibling handles within a line card qualify under the current length filter (all of them if no
	// length filter is active). Single source of truth used both for card visibility and swatch narrowing.
	function qualifyingSiblingHandles(card, state) {
		var handles = Object.keys(card.siblings);

		if (state.length.size === 0) return handles;

		return handles.filter(function (handle) {
			var lengths = (card.siblings[handle].lengths || []).map(normalize);
			return lengths.some(function (l) { return state.length.has(l); });
		});
	}

	function lineCardMatches(card, state) {
		if (state.drop.size > 0 && !state.drop.has(card.drop)) return false;
		return qualifyingSiblingHandles(card, state).length > 0;
	}

	function teeballCardMatches(card, state) {
		if (state.drop.size > 0 && !state.drop.has(card.drop)) return false;
		if (state.length.size > 0 && !card.lengths.some(function (l) { return state.length.has(l); })) return false;
		return true;
	}

	function cardMatchesState(card, state) {
		if (card.type === 'line') return lineCardMatches(card, state);
		if (card.type === 'teeball') return teeballCardMatches(card, state);
		return false; // 'hide-on-filter' cards are handled outside the spec-matching path.
	}

	// Grid reshaping: hide/show individual swatch buttons within a visible line card based on which sibling
	// handles qualify under the active length filter, and auto-swap the displayed default edition if it no
	// longer qualifies. Called for every visible line card on every filter change, including when the length
	// filter is cleared (qualifying == every handle), which restores the full swatch row.
	function narrowSwatches(card, qualifying) {
		var qualifyingSet = new Set(qualifying);

		Object.keys(card.siblings).forEach(function (handle) {
			var label = card.el.querySelector('[data-edition-handle="' + handle + '"]');
			if (!label) return;

			var input = card.el.querySelector('input[data-product-handle="' + handle + '"]');
			var qualifies = qualifyingSet.has(handle);

			label.hidden = !qualifies;
			if (input) input.disabled = !qualifies;
		});

		if (!qualifyingSet.has(card.currentHandle) && qualifying.length > 0) {
			swapToSibling(card, qualifying[0]);
		}
	}

	function swapToSibling(card, handle) {
		var radio = card.el.querySelector('input[type="radio"][data-product-handle="' + handle + '"]');
		if (!radio) return;

		radio.checked = true;
		radio.dispatchEvent(new Event('change', { bubbles: true }));
		card.currentHandle = handle;
	}

	function applyFilters(cards, filterInputs, emptyState) {
		var state = readActiveState(filterInputs);
		var anyActive = state.length.size > 0 || state.drop.size > 0;
		var specMatchCount = 0;

		cards.forEach(function (card) {
			if (card.type === 'hide-on-filter') {
				card.el.hidden = anyActive;
				return;
			}

			if (card.type === 'line') {
				var matches = lineCardMatches(card, state);
				card.el.hidden = !matches;

				if (matches) {
					specMatchCount++;
					narrowSwatches(card, qualifyingSiblingHandles(card, state));
				}
				return;
			}

			if (card.type === 'teeball') {
				var teeballMatches = teeballCardMatches(card, state);
				card.el.hidden = !teeballMatches;
				if (teeballMatches) specMatchCount++;
			}
		});

		if (emptyState) emptyState.hidden = specMatchCount > 0;

		updateDynamicNarrowing(cards, filterInputs, state);
	}

	// For each unchecked filter option, test whether selecting it (combined with whatever is currently active
	// in the OTHER facet — deliberately ignoring this option's own facet, since checkboxes within a facet are
	// OR'd together and can only add matches) would produce at least one line/teeball card match. Zero matches
	// -> visually disable the option so it can't be picked into a dead-end combination.
	function updateDynamicNarrowing(cards, filterInputs, liveState) {
		var specCards = cards.filter(function (c) { return c.type === 'line' || c.type === 'teeball'; });

		filterInputs.forEach(function (input) {
			if (input.checked) {
				input.disabled = false;
				input.closest('.usa-bats-grid__filter-option').classList.remove('is-unavailable');
				return;
			}

			var facet = input.getAttribute('data-usa-bats-filter');
			var testState = {
				length: new Set(liveState.length),
				drop: new Set(liveState.drop)
			};
			testState[facet] = new Set([normalize(input.value)]);

			var hasMatch = specCards.some(function (card) { return cardMatchesState(card, testState); });

			input.disabled = !hasMatch;
			input.closest('.usa-bats-grid__filter-option').classList.toggle('is-unavailable', !hasMatch);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
